import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/icons';
import type { ApiError } from '@/api/client';
import type { AsyncState } from '@/hooks';
import styles from './platform-ui.module.css';

/**
 * The one place a platform screen is allowed to fail.
 *
 * The endpoints behind this console are being written right now, so "this route
 * does not exist yet" is a normal, expected outcome and it must not look like a
 * bug, a blank card, or a spinner that never stops. Each failure gets its own
 * sentence — not deployed, not permitted, unreachable, or broken — because the
 * operator's next action is different in each case, and the raw status stays
 * available for whoever has to open a ticket.
 */

interface FailureCopy {
  titleKey: string;
  bodyKey: string;
  icon: IconName;
}

function classify(error: ApiError): FailureCopy {
  if (error.isMissingEndpoint) {
    return { titleKey: 'platform.failure.missing.title', bodyKey: 'platform.failure.missing.body', icon: 'info' };
  }
  if (error.isForbidden) {
    return { titleKey: 'platform.failure.forbidden.title', bodyKey: 'platform.failure.forbidden.body', icon: 'noAccess' };
  }
  if (error.isUnreachable) {
    return { titleKey: 'platform.failure.unreachable.title', bodyKey: 'platform.failure.unreachable.body', icon: 'warning' };
  }
  return { titleKey: 'platform.failure.generic.title', bodyKey: 'platform.failure.generic.body', icon: 'error' };
}

export function FailureNotice({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  const { t } = useTranslation();
  const copy = classify(error);

  return (
    <div className={styles.failure} role="alert">
      <span className={styles.failureIcon} aria-hidden="true">
        <Icon name={copy.icon} size={20} />
      </span>
      <div className={styles.failureText}>
        <p className={styles.failureTitle}>{t(copy.titleKey)}</p>
        <p className={styles.failureBody}>{t(copy.bodyKey)}</p>
        <details className={styles.failureDetails}>
          <summary>{t('error.technicalDetails')}</summary>
          <code>
            {error.status || t('platform.failure.noStatus')} · {error.path}
            {error.message ? ` · ${error.message}` : ''}
          </code>
        </details>
      </div>
      {onRetry && (
        <button type="button" className={styles.failureRetry} onClick={onRetry}>
          <Icon name="refresh" size={15} />
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

interface AsyncPanelProps<T> {
  state: AsyncState<T>;
  onRetry?: () => void;
  /** Rendered instead of children when the resolved value has nothing in it. */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyBody?: string;
  /** Replaces the default text-only empty notice, e.g. an illustrated <EmptyState>. */
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}

export function AsyncPanel<T>({ state, onRetry, isEmpty, emptyTitle, emptyBody, empty, children }: AsyncPanelProps<T>) {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        <span className={styles.loadingBar} aria-hidden="true" />
        <span className={styles.loadingBar} aria-hidden="true" />
        <span className={styles.srOnly}>{t('common.loading')}</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return <FailureNotice error={state.error} onRetry={onRetry} />;
  }

  if (isEmpty?.(state.data)) {
    if (empty) return <>{empty}</>;
    return (
      <div className={styles.empty} role="status">
        <p className={styles.emptyTitle}>{emptyTitle ?? t('common.noResults')}</p>
        {emptyBody && <p className={styles.emptyBody}>{emptyBody}</p>}
      </div>
    );
  }

  return <>{children(state.data)}</>;
}

interface PanelProps {
  title: string;
  icon?: IconName;
  /** A short count or status shown beside the title, e.g. "7 need attention". */
  meta?: ReactNode;
  actions?: ReactNode;
  /** Raises the panel visually when its contents are what should be acted on. */
  emphasis?: 'default' | 'attention';
  children: ReactNode;
}

export function Panel({ title, icon, meta, actions, emphasis = 'default', children }: PanelProps) {
  return (
    <section className={`${styles.panel} ${emphasis === 'attention' ? styles.panelAttention : ''}`}>
      <header className={styles.panelHeader}>
        {icon && (
          <span className={styles.panelIcon} aria-hidden="true">
            <Icon name={icon} size={17} />
          </span>
        )}
        <h2 className={styles.panelTitle}>{title}</h2>
        {meta && <span className={styles.panelMeta}>{meta}</span>}
        {actions && <div className={styles.panelActions}>{actions}</div>}
      </header>
      <div className={styles.panelBody}>{children}</div>
    </section>
  );
}
